import React, { useEffect, useState } from 'react';
import { translate } from 'react-jhipster';
import { toast } from 'react-toastify';
import { useAppDispatch, useAppSelector } from 'app/config/store';
import Box from '@mui/material/Box';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import AddIcon from '@mui/icons-material/Add';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import EditIcon from '@mui/icons-material/Edit';
import { IRebateSetting } from 'app/shared/model/rebate-setting.model';
import {
  getEntitiesByBank,
  createEntity,
  duplicateEntity,
  renameEntity,
  activateEntity,
  deactivateEntity,
} from 'app/entities/rebate-setting/rebate-setting.reducer';

type DialogMode = 'create' | 'duplicate' | 'rename' | null;

interface ISettingPickerProps {
  bankId: number | string;
  commodityType: string;
  selectedSettingId: number | string | '';
  onSelectedSettingIdChange: (id: number | string) => void;
}

/**
 * Lists a bank's rebate settings for one commodity type, lets the caller switch between them, and
 * offers add/duplicate/rename/activate-deactivate actions (ALSHMOKH-56). Used by the Rebate wizard
 * (bank-rebate.tsx) only — the Fee Setting screen has no picker, since every setting of one bank +
 * commodity type shares a single rebate fee configuration.
 */
export const SettingPicker = ({ bankId, commodityType, selectedSettingId, onSelectedSettingIdChange }: ISettingPickerProps) => {
  const dispatch = useAppDispatch();
  const settings: IRebateSetting[] = useAppSelector(state => state.rebateSetting.entities);
  const [dialogMode, setDialogMode] = useState<DialogMode>(null);
  const [dialogName, setDialogName] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (bankId && commodityType) {
      dispatch(getEntitiesByBank({ bankId, commodityType }));
    }
  }, [bankId, commodityType]);

  // Auto-select the first setting once the list loads, if nothing is selected yet (or the
  // previously selected one no longer belongs to this commodity type's list).
  useEffect(() => {
    if (settings.length > 0 && !settings.some(s => s.id === selectedSettingId)) {
      onSelectedSettingIdChange(settings[0].id);
    }
  }, [settings]);

  const selected = settings.find(s => s.id === selectedSettingId);

  const openDialog = (mode: DialogMode) => {
    if (mode === 'duplicate' && selected) {
      setDialogName(translate('comexApp.bank.rebate.setting.duplicateDefaultName', { name: selected.name }));
    } else if (mode === 'rename' && selected) {
      setDialogName(selected.name || '');
    } else {
      setDialogName('');
    }
    setDialogMode(mode);
  };

  const closeDialog = () => {
    if (!submitting) {
      setDialogMode(null);
    }
  };

  const handleDialogSubmit = async () => {
    const name = dialogName.trim();
    if (!name) {
      return;
    }
    setSubmitting(true);
    try {
      if (dialogMode === 'create') {
        const result = await dispatch(createEntity({ bankId, commodityType, name })).unwrap();
        onSelectedSettingIdChange(result.data.id);
        toast.success(translate('comexApp.bank.rebate.setting.createSuccess'));
      } else if (dialogMode === 'duplicate' && selected) {
        const result = await dispatch(duplicateEntity({ id: selected.id, name })).unwrap();
        onSelectedSettingIdChange(result.data.id);
        toast.success(translate('comexApp.bank.rebate.setting.duplicateSuccess'));
      } else if (dialogMode === 'rename' && selected) {
        await dispatch(renameEntity({ id: selected.id, name })).unwrap();
        toast.success(translate('comexApp.bank.rebate.setting.renameSuccess'));
      }
      setDialogMode(null);
    } catch (error) {
      toast.error(translate('comexApp.bank.rebate.setting.saveError'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleToggleActive = () => {
    if (!selected) {
      return;
    }
    dispatch(selected.isActive ? deactivateEntity(selected.id) : activateEntity(selected.id));
  };

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3, flexWrap: 'wrap' }}>
      <FormControl size="small" sx={{ minWidth: 220 }}>
        <Select
          value={selectedSettingId || ''}
          onChange={e => onSelectedSettingIdChange(e.target.value)}
          displayEmpty
          data-cy="rebateSettingPicker"
        >
          {settings.length === 0 && (
            <MenuItem value="" disabled>
              {translate('comexApp.bank.rebate.setting.none')}
            </MenuItem>
          )}
          {settings.map(s => (
            <MenuItem key={s.id} value={s.id}>
              {s.name}
              {!s.isActive ? ` (${translate('comexApp.bank.rebate.setting.inactive')})` : ''}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <Tooltip title={translate('comexApp.bank.rebate.setting.add')}>
        <IconButton size="small" onClick={() => openDialog('create')} data-cy="addRebateSetting">
          <AddIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Tooltip title={translate('comexApp.bank.rebate.setting.duplicate')}>
        <span>
          <IconButton size="small" disabled={!selected} onClick={() => openDialog('duplicate')} data-cy="duplicateRebateSetting">
            <ContentCopyIcon fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>
      <Tooltip title={translate('comexApp.bank.rebate.setting.rename')}>
        <span>
          <IconButton size="small" disabled={!selected} onClick={() => openDialog('rename')} data-cy="renameRebateSetting">
            <EditIcon fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>
      {selected && (
        <Button size="small" onClick={handleToggleActive} sx={{ textTransform: 'none' }} data-cy="toggleRebateSettingActive">
          {translate(`comexApp.bank.rebate.setting.${selected.isActive ? 'deactivate' : 'activate'}`)}
        </Button>
      )}

      <Dialog open={dialogMode !== null} onClose={closeDialog}>
        <DialogTitle>{dialogMode && translate(`comexApp.bank.rebate.setting.dialog.${dialogMode}Title`)}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            margin="dense"
            label={translate('comexApp.bank.rebate.setting.name')}
            value={dialogName}
            onChange={e => setDialogName(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog} disabled={submitting}>
            {translate('entity.action.cancel')}
          </Button>
          <Button onClick={handleDialogSubmit} variant="contained" disabled={submitting || !dialogName.trim()}>
            {translate('entity.action.save')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SettingPicker;
